import { Building2, MapPin, BedDouble, Bath, UserX, Pencil, Eye, CheckCircle2 } from "lucide-react"
import { Link, useNavigate } from "react-router-dom"
import React from "react"
import { Card, CardContent } from "@/components/ui/card"
import { StatusBadge } from "@/components/shared/StatusBadge"
import { Button } from "@/components/ui/button"
import { useCurrency } from "@/hooks/useCurrency"
import type { Property } from "@rentbase/shared"

interface PropertyCardProps {
  property: Property
}

export function PropertyCard({ property }: PropertyCardProps) {
  const navigate = useNavigate()
  const { format } = useCurrency()

  const activeLeasesCount = (property as any).active_leases_count || 0
  const coverPhotoUrl = (property as any).cover_photo_url

  return (
    <Card
      className="group rounded-[1.5rem] border-border/40 bg-white/60 dark:bg-card/40 backdrop-blur-xl shadow-soft hover:shadow-glow hover:-translate-y-1 transition-all duration-300 cursor-pointer overflow-hidden p-[6px] flex flex-col h-full"
      onClick={() => navigate(`/properties/${property.id}`)}
    >
      {/* Cover photo */}
      <div className="w-full aspect-[16/10] bg-slate-100 dark:bg-slate-800/50 rounded-2xl overflow-hidden relative mb-4 shrink-0">
        {coverPhotoUrl ? (
          <>
            <img
              src={coverPhotoUrl}
              alt={property.name}
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700 ease-out"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/10 to-transparent opacity-80" />
          </>
        ) : (
          <>
            <div className="absolute inset-0 flex items-center justify-center">
              <Building2 className="h-10 w-10 text-slate-300 dark:text-slate-600" />
            </div>
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-80" />
          </>
        )}

        {/* Absolute details over image */}
        <div className="absolute bottom-3 left-3 right-3 flex justify-between items-end">
          <div className="flex flex-wrap gap-2 text-white/90">
            {property.bedrooms != null && (
              <span className="flex items-center gap-1 text-xs font-semibold bg-black/30 backdrop-blur-md px-2 py-1 rounded-md">
                <BedDouble className="h-3.5 w-3.5" />
                {property.bedrooms} bd
              </span>
            )}
            {property.bathrooms != null && (
              <span className="flex items-center gap-1 text-xs font-semibold bg-black/30 backdrop-blur-md px-2 py-1 rounded-md">
                <Bath className="h-3.5 w-3.5" />
                {property.bathrooms} ba
              </span>
            )}
          </div>
          <StatusBadge status={property.status} />
        </div>
      </div>

      <CardContent className="p-3 pt-0 flex-1 flex flex-col">
        <div className="flex items-start justify-between gap-3 mb-1">
          <div className="flex items-center gap-2 min-w-0">
            <h3 className="font-bold text-lg text-foreground truncate tracking-tight">{property.name}</h3>
          </div>
        </div>

        {property.city && (
          <div className="flex items-center gap-1.5 text-sm text-muted-foreground mb-4 font-medium">
            <MapPin className="h-3.5 w-3.5 shrink-0 text-primary/70" />
            <span className="truncate">{property.city}</span>
            <span className="text-slate-300 dark:text-slate-600 mx-1">•</span>
            <span className="capitalize text-slate-500 line-clamp-1">{property.type?.replace(/_/g, " ")}</span>
          </div>
        )}

        {/* Info Rows */}
        <div className="mt-auto flex flex-col gap-3">
          <div className="p-3 rounded-xl bg-slate-50 dark:bg-slate-900/50 border border-slate-100 dark:border-slate-800">
            <div className="flex justify-between items-center mb-2">
              <span className="text-xs font-medium text-slate-500 uppercase tracking-wider">Lease Status</span>
              {activeLeasesCount > 0 ? (
                <span className="text-sm font-semibold text-emerald-600 dark:text-emerald-400">
                  {activeLeasesCount} Active {activeLeasesCount === 1 ? 'Lease' : 'Leases'}
                </span>
              ) : (
                <span className="text-sm font-semibold text-amber-600">Vacant</span>
              )}
            </div>

            {activeLeasesCount > 0 ? (
              <div className="flex items-center gap-2 text-sm text-emerald-600 dark:text-emerald-400 font-medium">
                <CheckCircle2 className="h-4 w-4" />
                <span>{activeLeasesCount} Tenant{activeLeasesCount === 1 ? '' : 's'}</span>
              </div>
            ) : (
              <div className="flex items-center gap-2 text-sm text-slate-400 font-medium">
                <UserX className="h-4 w-4" />
                <span>No active tenants</span>
              </div>
            )}
          </div>

          <div className="flex gap-2">
            <button
              onClick={(e) => { e.stopPropagation(); navigate(`/properties/${property.id}`); }}
              className="flex-1 flex items-center justify-center gap-1.5 py-2 px-3 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg text-sm font-medium text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors"
            >
              <Eye className="h-3.5 w-3.5" />
              View Details
            </button>
          </div>
        </div>

      </CardContent>
    </Card>
  )
}
